import {useRef, useState} from 'react'
import {clsx} from 'clsx'
import {ChevronDownIcon, ChevronUpIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, TextInput} from '@primer/react'

import {
  PullRequestProjectFieldType,
  type PullRequestProject,
  type PullRequestProjectField,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/projects'
import {useUpdateProjectField} from '../../mutations/use-update-project-field'
import {MutationFailureBanner} from '../FailureBanner'
import styles from './Projects.module.css'

/**
 * Toggles display of a project's "additional fields" -- the user-defined fields (beyond the
 * built-in "Status" field) configured on the project -- mirroring the Rails sidebar's
 * `collapsible-sidebar-widget` "+N more" disclosure in `issues/sidebar/_memex_project_item`.
 */
export function ProjectFieldsToggle({
  fieldCount,
  expanded,
  onToggle,
  className,
}: {
  fieldCount: number
  expanded: boolean
  onToggle: () => void
  className?: string
}) {
  return (
    <Button
      variant="invisible"
      size="small"
      className={clsx(styles.fieldsToggle, className)}
      trailingVisual={expanded ? ChevronUpIcon : ChevronDownIcon}
      onClick={onToggle}
      aria-expanded={expanded}
    >
      {expanded ? 'Show less' : `+${fieldCount} more`}
    </Button>
  )
}

/** Renders every one of a project's additional fields, each editable in place. */
export function ProjectFieldsList({project}: {project: PullRequestProject}) {
  return (
    <ul className={styles.fieldsList}>
      {project.fields.map(field => (
        <li key={field.fieldId} className={styles.fieldRow}>
          <span className={styles.fieldLabel}>{field.name}</span>
          <ProjectFieldValue project={project} field={field} />
        </li>
      ))}
    </ul>
  )
}

function ProjectFieldValue({project, field}: {project: PullRequestProject; field: PullRequestProjectField}) {
  const readonly = !project.viewerCanUpdate

  switch (field.type) {
    case PullRequestProjectFieldType.TEXT:
      return (
        <InlineTextFieldValue
          project={project}
          field={field}
          readonly={readonly}
          type="text"
          value={field.textValue}
          toMutationValue={value => (value === '' ? null : value)}
        />
      )
    case PullRequestProjectFieldType.NUMBER:
      return (
        <InlineTextFieldValue
          project={project}
          field={field}
          readonly={readonly}
          type="number"
          value={field.numberValue === undefined ? '' : String(field.numberValue)}
          toMutationValue={value => (value === '' ? null : Number(value))}
        />
      )
    case PullRequestProjectFieldType.DATE:
      return (
        <InlineTextFieldValue
          project={project}
          field={field}
          readonly={readonly}
          type="date"
          value={field.dateValue}
          toMutationValue={value => (value === '' ? null : value)}
        />
      )
    case PullRequestProjectFieldType.SINGLE_SELECT:
      return <SingleSelectFieldValue project={project} field={field} readonly={readonly} />
    case PullRequestProjectFieldType.MULTI_SELECT:
      return <MultiSelectFieldValue project={project} field={field} readonly={readonly} />
    case PullRequestProjectFieldType.ITERATION:
      return <IterationFieldValue project={project} field={field} readonly={readonly} />
    default:
      return null
  }
}

interface FieldValueProps {
  project: PullRequestProject
  field: PullRequestProjectField
  readonly: boolean
}

/**
 * Text/number/date field editor: a plain input that commits its value on blur, mirroring the
 * Rails sidebar's auto-saving `sidebar-memex-input` fields.
 */
function InlineTextFieldValue({
  project,
  field,
  readonly,
  type,
  value,
  toMutationValue,
}: FieldValueProps & {
  type: 'text' | 'number' | 'date'
  value: string
  toMutationValue: (value: string) => string | number | null
}) {
  const mutation = useUpdateProjectField()
  const {mutate, isPending} = mutation
  const inputRef = useRef<HTMLInputElement>(null)
  const [draft, setDraft] = useState(value)
  const [previousValue, setPreviousValue] = useState(value)

  // Resyncs the draft whenever the authoritative value changes -- e.g. after a successful commit,
  // or after an errored mutation rolls the optimistic update back -- without remounting (which
  // would otherwise create a fresh mutation and lose its error/pending state). Calling setState
  // directly during render (rather than in an effect) is the recommended way to adjust state in
  // response to a prop change: https://react.dev/learn/you-might-not-need-an-effect
  if (value !== previousValue) {
    setPreviousValue(value)
    setDraft(value)
  }

  if (readonly) {
    return <span className={styles.fieldValueText}>{value || 'None'}</span>
  }

  return (
    <>
      <TextInput
        ref={inputRef}
        block
        size="small"
        type={type}
        value={draft}
        disabled={isPending}
        aria-label={field.name}
        onChange={e => setDraft(e.target.value)}
        onBlur={e => {
          if (e.currentTarget.validity.badInput) {
            e.currentTarget.reportValidity()
            return
          }
          if (draft !== value) {
            mutate({
              projectId: project.id,
              itemId: project.itemId,
              fieldId: field.fieldId,
              value: toMutationValue(draft),
            })
          }
        }}
      />
      <MutationFailureBanner
        mutation={mutation}
        title={`Failed to update ${field.name}`}
        returnFocusRef={inputRef}
        className={styles.mutationError}
      />
    </>
  )
}

function SingleSelectFieldValue({project, field, readonly}: FieldValueProps) {
  const mutation = useUpdateProjectField()
  const {mutate, isPending} = mutation
  const buttonRef = useRef<HTMLButtonElement>(null)
  const currentOption = field.options.find(option => option.id === field.optionId)
  const label = currentOption?.name ?? 'None'

  if (readonly) {
    return <span className={styles.fieldValueText}>{label}</span>
  }

  return (
    <>
      <ActionMenu>
        <ActionMenu.Button
          ref={buttonRef}
          variant="invisible"
          size="small"
          labelWrap
          trailingAction={TriangleDownIcon}
          className={styles.fieldValueButton}
          aria-label={`${field.name}: ${label}`}
          inactive={isPending || undefined}
        >
          {label}
        </ActionMenu.Button>
        <ActionMenu.Overlay align="start">
          <ActionList selectionVariant="single">
            <ActionList.Item
              selected={!field.optionId}
              onSelect={() => {
                if (isPending || !field.optionId) return
                mutate({projectId: project.id, itemId: project.itemId, fieldId: field.fieldId, value: null})
              }}
            >
              None
            </ActionList.Item>
            {field.options.map(option => (
              <ActionList.Item
                key={option.id}
                selected={option.id === field.optionId}
                onSelect={() => {
                  if (isPending || option.id === field.optionId) return
                  mutate({projectId: project.id, itemId: project.itemId, fieldId: field.fieldId, value: option.id})
                }}
              >
                {option.name}
              </ActionList.Item>
            ))}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <MutationFailureBanner
        mutation={mutation}
        title={`Failed to update ${field.name}`}
        returnFocusRef={buttonRef}
        className={styles.mutationError}
      />
    </>
  )
}

function MultiSelectFieldValue({project, field, readonly}: FieldValueProps) {
  const mutation = useUpdateProjectField()
  const {mutate, isPending} = mutation
  const buttonRef = useRef<HTMLButtonElement>(null)
  const selectedOptions = field.options.filter(option => field.optionIds.includes(option.id))
  const label = selectedOptions.length > 0 ? selectedOptions.map(option => option.name).join(', ') : 'None'

  if (readonly) {
    return <span className={styles.fieldValueText}>{label}</span>
  }

  return (
    <>
      <ActionMenu>
        <ActionMenu.Button
          ref={buttonRef}
          variant="invisible"
          size="small"
          labelWrap
          trailingAction={TriangleDownIcon}
          className={styles.fieldValueButton}
          aria-label={`${field.name}: ${label}`}
          inactive={isPending || undefined}
        >
          {label}
        </ActionMenu.Button>
        <ActionMenu.Overlay align="start">
          <ActionList selectionVariant="multiple">
            {field.options.map(option => (
              <ActionList.Item
                key={option.id}
                selected={field.optionIds.includes(option.id)}
                disabled={isPending}
                onSelect={() => {
                  if (isPending) return
                  const next = field.optionIds.includes(option.id)
                    ? field.optionIds.filter(id => id !== option.id)
                    : [...field.optionIds, option.id]
                  mutate({projectId: project.id, itemId: project.itemId, fieldId: field.fieldId, value: next})
                }}
              >
                {option.name}
              </ActionList.Item>
            ))}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <MutationFailureBanner
        mutation={mutation}
        title={`Failed to update ${field.name}`}
        returnFocusRef={buttonRef}
        className={styles.mutationError}
      />
    </>
  )
}

function IterationFieldValue({project, field, readonly}: FieldValueProps) {
  const mutation = useUpdateProjectField()
  const {mutate, isPending} = mutation
  const buttonRef = useRef<HTMLButtonElement>(null)
  const currentIteration = field.iterations.find(iteration => iteration.id === field.iterationId)
  const label = currentIteration?.title || 'None'

  if (readonly) {
    return <span className={styles.fieldValueText}>{label}</span>
  }

  return (
    <>
      <ActionMenu>
        <ActionMenu.Button
          ref={buttonRef}
          variant="invisible"
          size="small"
          labelWrap
          trailingAction={TriangleDownIcon}
          className={styles.fieldValueButton}
          aria-label={`${field.name}: ${label}`}
          inactive={isPending || undefined}
        >
          {label}
        </ActionMenu.Button>
        <ActionMenu.Overlay align="start">
          <ActionList selectionVariant="single">
            <ActionList.Item
              selected={!field.iterationId}
              onSelect={() => {
                if (isPending || !field.iterationId) return
                mutate({projectId: project.id, itemId: project.itemId, fieldId: field.fieldId, value: null})
              }}
            >
              None
            </ActionList.Item>
            {field.iterations.map(iteration => (
              <ActionList.Item
                key={iteration.id}
                selected={iteration.id === field.iterationId}
                onSelect={() => {
                  if (isPending || iteration.id === field.iterationId) return
                  mutate({projectId: project.id, itemId: project.itemId, fieldId: field.fieldId, value: iteration.id})
                }}
              >
                {iteration.title}
              </ActionList.Item>
            ))}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <MutationFailureBanner
        mutation={mutation}
        title={`Failed to update ${field.name}`}
        returnFocusRef={buttonRef}
        className={styles.mutationError}
      />
    </>
  )
}
